import { useEffect, useMemo, useState } from "react";
import { mutate, useApi, verifyPath } from "../api/client";
import type { VerifyField, VerifyForm } from "../api/types";
import { Empty, ErrorState, Loading } from "./States";
import { fmtDateTime } from "../lib/format";

type Verdict = "correct" | "wrong" | "absent";

interface Draft {
  verdict: Verdict | null;
  expected: string;
  note: string;
}

const asText = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

/**
 * Record what a document actually says, field by field.
 *
 * This is the human half of the eval harness, which has existed since migration
 * 027 and has never been usable outside a CLI. Everything the platform can
 * later learn from corrections is built on the judgements captured here.
 *
 * The screen does one thing and resists doing more: it is a form for saying
 * "this value is right", "this one is wrong and here is the right one", or
 * "this field is not on the page at all". That third answer is the reason the
 * table exists — without it there is no way to distinguish a model that
 * invented a value from a field nobody checked, and on a KYC file an invented
 * value is fabricated evidence.
 */
export function VerifyFields({ id }: { id: string }) {
  const path = verifyPath(id);
  const { data, error, loading, reload } = useApi<VerifyForm>(path);

  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);

  // Reset whenever the server's answer changes, which covers the first load and
  // the reload after a save. Existing verdicts are NOT pre-loaded into the
  // draft: a reviewer revisiting a document should be re-deciding, not nudging
  // a form that already agrees with itself.
  useEffect(() => {
    setDraft({});
    setSavedCount(null);
  }, [data]);

  const set = (field: string, patch: Partial<Draft>) => {
    setSaveError(null);
    setSavedCount(null);
    setDraft((prev) => {
      const base: Draft = prev[field] ?? { verdict: null, expected: "", note: "" };
      return { ...prev, [field]: { ...base, ...patch } };
    });
  };

  const pending = useMemo(
    () => Object.entries(draft).filter(([, d]) => d.verdict !== null),
    [draft],
  );

  // Checked here as well as server-side, because the server refuses the whole
  // batch when any entry is bad and a reviewer who filled in eight fields
  // should not lose them to a ninth.
  const incomplete = pending.filter(
    ([, d]) => d.verdict === "wrong" && !d.expected.trim(),
  );

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await mutate<{ verified: number }>(path, "PUT", {
        fields: pending.map(([field, d]) => ({
          field,
          verdict: d.verdict,
          expected_value: d.verdict === "wrong" ? d.expected.trim() : undefined,
          note: d.note.trim() || undefined,
        })),
      });
      setSavedCount(res.verified);
      reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading what="verification form" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  if (!data.has_extraction) {
    return (
      <Empty
        title="Nothing to verify"
        hint="This document has no agent extraction, so there are no values to check against the page."
      />
    );
  }

  // Scored fields first: they are the ones that move the agent's score, and a
  // reviewer with ten minutes should spend them there rather than working
  // alphabetically through twenty fields.
  const ordered = [...data.fields].sort((a, b) =>
    a.scored === b.scored ? a.field.localeCompare(b.field) : a.scored ? -1 : 1,
  );

  return (
    <>
      <div className="notice">
        <strong>Check each value against the document itself</strong>, not against
        how plausible it looks — the model's answer is filled in below to save
        typing, and agreeing with it is the easy mistake. Open the{" "}
        <em>Original document</em> tab alongside this one.
      </div>

      {!data.can_verify ? (
        <div className="notice notice-warn">
          Only an administrator can record verified values. Your name is stored
          against each judgement as the person accountable for it.
        </div>
      ) : null}

      <div className="verify-meta">
        <span>
          {data.verified_count} of {data.field_count} field(s) already verified
        </span>
        {data.agent_confidence !== null ? (
          <span>agent confidence {data.agent_confidence}%</span>
        ) : null}
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">What the model read</th>
              <th scope="col" style={{ width: 290 }}>
                Your judgement
              </th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((f) => (
              <VerifyRow
                key={f.field}
                f={f}
                draft={draft[f.field]}
                disabled={!data.can_verify || saving}
                onChange={(patch) => set(f.field, patch)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {saveError ? <div className="notice notice-error">{saveError}</div> : null}
      {savedCount !== null && pending.length === 0 ? (
        <div className="notice">
          Saved {savedCount} judgement{savedCount === 1 ? "" : "s"}.
        </div>
      ) : null}

      {data.can_verify ? (
        <div className="form-actions">
          <span className="muted actions-note">
            {pending.length === 0
              ? "Mark a field correct, wrong or not present to record it."
              : incomplete.length
                ? `${incomplete.length} field(s) marked wrong still need the correct value.`
                : `${pending.length} judgement${pending.length === 1 ? "" : "s"} ready to save.`}
          </span>
          <button
            className="btn"
            onClick={() => setDraft({})}
            disabled={pending.length === 0 || saving}
          >
            Clear
          </button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={pending.length === 0 || incomplete.length > 0 || saving}
          >
            {saving ? "Saving…" : `Save ${pending.length || ""}`.trim()}
          </button>
        </div>
      ) : null}
    </>
  );
}

function VerifyRow({
  f,
  draft,
  disabled,
  onChange,
}: {
  f: VerifyField;
  draft?: Draft;
  disabled: boolean;
  onChange: (patch: Partial<Draft>) => void;
}) {
  const chosen = draft?.verdict ?? null;
  const modelValue = asText(f.model_said);
  const empty = modelValue.trim() === "";

  return (
    <tr className={chosen ? "row-dirty" : undefined}>
      <td>
        <span className="mono">{f.field}</span>
        {f.scored ? (
          <span className="pill pill-warning" style={{ marginLeft: 8 }}>
            scored
          </span>
        ) : null}
        {/* What is already on file. Shown next to the field rather than in a
            separate column, because a reviewer needs it only when it exists. */}
        {f.verdict ? (
          <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
            {f.verdict === "absent"
              ? "verified: not on this document"
              : `verified: ${f.expected_value}`}{" "}
            — {f.verified_by}
            {f.verified_at ? `, ${fmtDateTime(f.verified_at)}` : ""}
          </div>
        ) : null}
      </td>

      <td className="value-cell">
        {empty ? (
          <span className="muted">nothing extracted</span>
        ) : (
          <>
            {modelValue}
            {f.model_confidence !== null ? (
              <div className="muted" style={{ fontSize: 11 }}>
                {f.model_confidence}% confident
              </div>
            ) : null}
          </>
        )}
      </td>

      <td>
        <div className="verdicts" role="group" aria-label={`Judgement for ${f.field}`}>
          {/* "Correct" is withheld when the model produced nothing: confirming
              an empty value as correct is meaningless, and the server refuses
              it. Offering a button that always fails is worse than not
              offering it. */}
          {!empty ? (
            <button
              type="button"
              className={`verdict-btn${chosen === "correct" ? " on good" : ""}`}
              disabled={disabled}
              onClick={() => onChange({ verdict: chosen === "correct" ? null : "correct" })}
            >
              Correct
            </button>
          ) : null}
          <button
            type="button"
            className={`verdict-btn${chosen === "wrong" ? " on bad" : ""}`}
            disabled={disabled}
            onClick={() => onChange({ verdict: chosen === "wrong" ? null : "wrong" })}
          >
            Wrong
          </button>
          <button
            type="button"
            className={`verdict-btn${chosen === "absent" ? " on" : ""}`}
            disabled={disabled}
            title="The document genuinely does not carry this field. Recording this is what makes an invented value detectable."
            onClick={() => onChange({ verdict: chosen === "absent" ? null : "absent" })}
          >
            Not on page
          </button>
        </div>

        {chosen === "wrong" ? (
          <input
            type="text"
            className="verdict-input"
            value={draft?.expected ?? ""}
            placeholder="The correct value, as printed"
            disabled={disabled}
            autoFocus
            onChange={(e) => onChange({ expected: e.target.value })}
          />
        ) : null}

        {chosen ? (
          <input
            type="text"
            className="verdict-input"
            value={draft?.note ?? ""}
            placeholder="Note (optional) — e.g. printed over a stamp"
            disabled={disabled}
            onChange={(e) => onChange({ note: e.target.value })}
          />
        ) : null}
      </td>
    </tr>
  );
}
